import axios from "axios";
import dayjs from "dayjs";
import { previewAdapter } from "./previewAdapter";
import { resolveApiBaseUrl } from "./apiBase";

const isPreview = process.env.REACT_APP_UI_PREVIEW === "true";

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  ...(isPreview ? { adapter: previewAdapter } : {}),
});

// Логин

export async function loginUser({
  username,
  password,
}: {
  username: string;
  password: string;
}) {
  return handleRequest(
    api.post("/auth/login/", { username, password }, { withCredentials: true })
  );
}

// Two-factor authentication (TOTP)
//
// The second login step relies on the short-lived `twofa_token` cookie (5 min)
// that the backend sets in response to /auth/login, so the body only carries
// the code — the user is identified by the cookie, not by a passed id.
export type TwoFactorStatus = {
  enabled: boolean;
  /** Secret created via /setup but not yet confirmed with a code. */
  pending_setup: boolean;
};

export type TwoFactorSetup = {
  secret: string;
  otpauth_url: string;
  issuer: string;
};

/** Second login step. Only called when /auth/login returned twofa_required. */
export async function loginVerify2fa(code: string) {
  return handleRequest(
    api.post("/auth/login/2fa", { code }, { withCredentials: true }),
  );
}

export const getTwoFactorStatus = async () => {
  return handleRequest(api.get("/auth/2fa/status", { withCredentials: true }));
};

/** Creates the secret and returns the otpauth link. Inactive until confirmed. */
export const setupTwoFactor = async () => {
  return handleRequest(
    api.post("/auth/2fa/setup", {}, { withCredentials: true }),
  );
};

export const enableTwoFactor = async (code: string) => {
  return handleRequest(
    api.post("/auth/2fa/enable", { code }, { withCredentials: true }),
  );
};

/** Requires a valid code so a hijacked session cannot drop the protection. */
export const disableTwoFactor = async (code: string) => {
  return handleRequest(
    api.post("/auth/2fa/disable", { code }, { withCredentials: true }),
  );
};

// Проверка на авторизацию

export async function checkAuth() {
  return handleRequest(api.get("/me/", { withCredentials: true }));
}

//общая функция для обработки
const handleRequest = async (request: any) => {
  try {
    const response = await request;
    return response;
  } catch (err) {
    console.error("Ошибка при выполнении запроса:", err);
    throw err;
  }
};

export const getRates = async () => {
  return handleRequest(api.get("/rates/merchant", { withCredentials: true }));
};
// Баланс
export const fetchBalance = async () => {
  return handleRequest(
    api.get("/merchants/balance", {
      withCredentials: true,
    })
  );
};
// Транзакции
export const getTrans = async (filters: any) => {
  return handleRequest(
    api.post(
      "/transactions/get_order_merchant/",
      { filters },
      {
        withCredentials: true,
      }
    )
  );
};
export const getLatest = async ({ page }: { page: number }) => {
  return handleRequest(
    api.post(
      "/transactions/get_list_merchant/",
      { page },
      { withCredentials: true }
    )
  );
};
// Вебхук
export const changeWebhookURL = async ({
  type,
  url,
}: {
  type: string;
  url: string;
}) => {
  const body = { [type]: url };

  return handleRequest(
    api.post("/callback_url/merchant", body, {
      withCredentials: true,
    })
  );
};
export const getWebhookURL = async () => {
  return handleRequest(
    api.get("/callback_url/merchant", {
      withCredentials: true,
    })
  );
};
// Transaction export
//
// The backend scopes the query to the merchant behind the session, so merchid
// and forMerch from the body are ignored — only dates, statuses, types and the
// output format matter here.

export type ExportFormat = "csv" | "xlsx";

export type MerchantExportFilters = {
  createdFrom?: string | null;
  createdTo?: string | null;
  updatedFrom?: string | null;
  updatedTo?: string | null;
  status?: string[];
  type?: string[];
  format?: ExportFormat;
};

/** Dates go out as dd.mm.yyyy: the backend parses them with strptime("%d.%m.%Y"). */
const toExportDay = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : undefined;
};

export const exportMerchantTransactions = async (
  filters: MerchantExportFilters = {},
) => {
  const body: Record<string, unknown> = {
    format: filters.format ?? "csv",
  };

  if (filters.status?.length) body.status = filters.status;
  if (filters.type?.length) body.type = filters.type;

  const createdFrom = toExportDay(filters.createdFrom);
  const createdTo = toExportDay(filters.createdTo);
  const updatedFrom = toExportDay(filters.updatedFrom);
  const updatedTo = toExportDay(filters.updatedTo);

  if (createdFrom) body.createdFrom = createdFrom;
  if (createdTo) body.createdTo = createdTo;
  if (updatedFrom) body.updatedFrom = updatedFrom;
  if (updatedTo) body.updatedTo = updatedTo;

  return handleRequest(
    api.post("/merchants/export/", body, {
      withCredentials: true,
      // xlsx is binary — parsing it as text corrupts the workbook.
      responseType: "blob",
    }),
  );
};

export const changePassword = async ({
  old_password,
  new_password,
}: {
  old_password: string;
  new_password: string;
}) => {
  return handleRequest(
    api.post(
      `/auth/change_password`,
      { old_password, new_password },
      { withCredentials: true }
    )
  );
};
